import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Layout, Button, Table, Pagination, Skeleton } from 'antd';
import { Link, useNavigate } from 'react-router-dom';

import { createUseStyles } from 'react-jss';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';

import store from 'src/redux/store/reduxStoreIndex';
import { getModelListRedux } from 'src/redux/actions/modelListActions';
import { getSettingsRedux } from 'src/redux/actions/settingsActions';
import NormalNavbar from 'src/views/navbar/normalNavbar';
import { MenuOutlined, AppstoreOutlined, SortAscendingOutlined, SortDescendingOutlined } from '@ant-design/icons';
import { addOpacityMenu } from 'src/utils/colorHelpers';
import TitleForTable from './titleForTable';
import AntdSearchBar from './antdSearchbar';
import CustomSortButton from './sortButton';
import { CustomFooter } from 'src/views/footer';
import CustomModelSelectCardList from './modelSelectCardList';
import OutsideAlerter from './OutsideAlerter';

const useStyles = createUseStyles({
	fraunhoferLogoDiv: {
		margin: 'auto',
		marginTop: '1vh',
		marginBottom: '0',
	},
	fraunhoferLogoImg: {
		display: 'block',
		margin: 'auto',
		maxWidth: '30vw',
		width: 'min(50vw, 50vh)',
	},
	viewIcon: {
		fontSize: '20px',
	},
	pager: {
		display: 'flex',
		justifyContent: 'center',
		marginTop: '15px',
	},
	loadSign: {
		position: 'relative',
		top: '250px',
		display: 'block',
		margin: 'auto',
	},
	viewButton: {
		width: '46px',
		height: '40px',
		backgroundColor: 'white',
		border: '1px solid #d9d9d9',
		cursor: 'pointer',
		borderRadius: '15px',
		float: 'right',
		'&:hover ~ $showMeaningView': {
			visibility: 'visible',
		},
	},
	sortButton: {
		width: '46px',
		height: '40px',
		backgroundColor: 'white',
		border: '1px solid #d9d9d9',
		cursor: 'pointer',
		borderRadius: '15px',
		float: 'right',
		'&:hover ~ $showMeaningSort': {
			visibility: 'visible',
		},
	},
	notFoundH1: {
		position: 'relative',
		fontSize: '16px',
		textAlign: 'center',
		bottom: '450px',
	},
	content: {
		marginTop: '3vh',
		marginLeft: '50vw',
		transform: ' translate(-50%, 0)',
		maxWidth: '1000px',
		width: '80vw',
		textAlign: 'center',
	},
	sortingDiv: {
		width: '240px',
		height: '240px',
	},
	buttonDiv: {
		display: 'flex',
	},
	buttonDivChild: {
		position: 'relative',
		left: '5px',
		marginLeft: '5px',
		marginRight: '5px',
		display: 'flex',
		justifyContent: 'center',
	},
	showMeaningSort: {
		position: 'absolute',
		top: '30px',
		pointerEvents: 'none',
		visibility: 'hidden',
		transitionProperty: 'visibility',
		transitionDelay: '0.2s',
	},
	showMeaningView: {
		position: 'absolute',
		top: '30px',
		pointerEvents: 'none',
		visibility: 'hidden',
		transitionProperty: 'visibility',
		transitionDelay: '0.2s',
	},
	showMeaningDiv: {
		position: 'relative',
		fontSize: '14px',
		width: 'auto',
		bottom: '-25px',
		zIndex: '10',
		color: 'white',
		backgroundColor: '#393939',
		padding: '5px',
		textAlign: 'center',
		whiteSpace: 'nowrap',
	},
	showMeaningPointer: {
		position: 'relative',
		backgroundColor: '#393939',
		top: '30px',
		zIndex: '10',
		width: '10px',
		height: '10px',
		margin: '0 auto',
		transformOrigin: 'center',
		transform: 'rotate(45deg)',
	},
	h2: {
		fontSize: 'min(40px, 24px)',
		display: 'inline-block',
		marginTop: '3vh',
	},
	h3: {
		fontSize: 'min(40px, 24px)',
		display: 'inline-block',
	},
	topDiv: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
	},
	list: {
		maxHeight: '60vh',
		overflowY: 'auto',
		background: '#fff',
	},
	listName: {
		flexGrow: 1,
	},
	listDate: {
		color: 'rgba(0, 0, 0, 0.65)',
		flexGrow: 0,
	},
	buttons: {
		width: '250px',
		marginTop: '25px',
	},
	tableHead: (props) => ({
		'& .ant-table-thead > tr > th': {
			backgroundColor: `${addOpacityMenu(props.model_LeftBack)}`,
			color: `${props.model_LeftText}`,
			fontWeight: 'bold',
		},
	}),
	tableLayout: {
		'& .ant-table': {
			fontSize: 'min(3vmax, 15px)',
		},
		'& .ant-table-tbody': {
			lineHeight: '17.7px',
		},

		'@global': {
			table: {
				borderCollapse: 'seperate',
				borderSpacing: '0 2px',
			},
			a: {
				height: '100%',
				display: 'block',
			},
			td: {
				width: '580px',
				overflow: 'hidden',
			},
		},
	},
	footer: {
		display: 'flex',
		justifyContent: 'center',
		marginTop: 'auto',
		background: 'transparent',
	},
});

// pageSize defined for pagination
const PAGE_SIZE = 10;

// antd schematic way to display the title
const renderTitle = (title) => <span>{title}</span>;

// antd schematic way to display the items
const renderItem = (title, id, link) => ({
	value: title,
	key: id,
	link,
	label: (
		<Link to={link}>
			<div
				style={{
					display: 'flex',
					justifyContent: 'space-between',
				}}
			>
				{title}
			</div>
		</Link>
	),
});

const ModelSelectView = (props) => {
	const { activeUser, modelList, settings } = props;
	const { menuColor: colors } = settings;
	const { user } = activeUser;
	const classes = useStyles(colors);
	const navigate = useNavigate();
	const { t, i18n } = useTranslation();
	const [sortOptions, setSortOptions] = useState(false);
	const [searchField, setSearchField] = useState('');
	const [currentPage, setCurrentPage] = useState(1);
	const [sortValue, setSortValue] = useState(false);
	const [anyModelPinned, setAnyModelPinned] = useState(false);
	const [filteredModels, setFilteredModels] = useState(modelList.items);

	// for the model select table
	const [tableData, setTableData] = useState(undefined);

	// for changing the value to determine which view
	const [viewValue, setViewValue] = useState(true);

	// function to invert the viewValue
	const changeViewValue = () => {
		setViewValue(!viewValue);
	};

	useEffect(() => {
		// if we loaded no models yet, we try to get them
		if (!modelList?.items[0]?._id && !modelList.isFetching) {
			store.dispatch(getModelListRedux());
		}
		document.body.style.height = 'auto';
		return () => {
			document.body.style.height = null;
		};
	}, [modelList.isFetching, modelList?.items]); // This is no optimal solution (https://overreacted.io/a-complete-guide-to-useeffect/#dont-lie-to-react-about-dependencies) but prob fine

	useEffect(() => {
		if (!settings.isFetching.modelView && settings.modelView.init) store.dispatch(getSettingsRedux('modelView'));
	}, [settings]);

	useEffect(() => {
		if (settings.modelView.allow) {
			const nonLinkModels = modelList.items.filter((m) => m.publicModel !== 'link');
			if (
				nonLinkModels.length === 1 &&
				(['public'].includes(nonLinkModels[0].publicModel) || user?.modelAccess?.includes(nonLinkModels[0].slug))
			) {
				navigate(`/model/${nonLinkModels[0].slug}/view/${nonLinkModels[0].homeView}`);
			}
		}
		if (settings.modelView.view) {
			setViewValue(false);
		}
	}, [settings.modelView, activeUser, modelList.items, user.modelAccess, navigate]);

	// function to update data from antd table
	const updateTableData = (query, sortedModels = null) => {
		let newFilteredModels = [];

		let models;
		if (sortedModels) {
			models = sortedModels;
		} else {
			models = modelList.items;
		}

		// function for calling when the searchField changes. When the State searchField is not in sync with the expected (happens in SearchOnChange) searchField.
		// You can use query as optional parameter.
		if (query) {
			models = models.filter((model) => model.displayTitle[i18n.language].toLowerCase().includes(query));
		}

		// filter active models
		if (models.length > 0) {
			newFilteredModels = models.filter((model) => model.isActive);
		}
		// filter out link only models
		if (newFilteredModels.length > 0) {
			// only for non-admins
			if (!activeUser.user.adminAccess) {
				newFilteredModels = newFilteredModels.filter((model) => model.publicModel !== 'link');
			}
		}

		// convert the old filtered models to newTableData
		const newTableData = [
			...newFilteredModels.map((m) => ({
				title: <TitleForTable model={m} />,
				date: m.latestVersionUpload,
				url: `/model/${m.slug}/view/${m.homeView}`,
				key: m._id,
			})),
		];
		setTableData(newTableData);
		setFilteredModels(newFilteredModels);
	};

	// default: ascending sort direction for date
	function sortDate({ ascending = true, descending } = {}) {
		const sortedModels = modelList.items.sort((a, b) => {
			const dateA = a.latestVersionUpload;
			const dateB = b.latestVersionUpload;
			if (descending || !ascending) {
				if (dateB < dateA) {
					return -1;
				}
				if (dateB > dateA) {
					return 1;
				}
				return 0;
			} else {
				if (dateA < dateB) {
					return -1;
				}
				if (dateA > dateB) {
					return 1;
				}
				return 0;
			}
		});
		return sortedModels;
	}

	function sortRelevance() {
		// sort after Date
		const sortedModels = modelList.items.sort((a, b) => {
			const dateA = a.latestVersionUpload;
			const dateB = b.latestVersionUpload;
			return dateB - dateA;
		});

		const sortedFavorites = sortedModels
			.filter((model) => model.isFavorite)
			.sort((a, b) => a.isFavorite - b.isFavorite);

		const nonFavorites = sortedModels.filter((model) => !model.isFavorite);

		return sortedFavorites.concat(nonFavorites);
	}

	// ascending sort direction for title
	const sortTitle = ({ ascending = true, descending } = {}) => {
		const sortedModels = modelList.items.sort((a, b) => {
			const titleA = a.displayTitle[i18n.language].toLowerCase();
			const titleB = b.displayTitle[i18n.language].toLowerCase();
			if (descending || !ascending) {
				return titleB.localeCompare(titleA);
			} else {
				return titleA.localeCompare(titleB);
			}
		});
		return sortedModels;
	};

	// opens and closes alternating
	const openCloseSortDiv = () => {
		if (sortOptions) {
			setSortOptions(false);
		} else {
			setSortOptions(true);
		}
	};

	// according to key of object the correct sort function will be called
	const callSorting = (value) => {
		setCurrentPage(1);
		switch (value) {
			case 1:
				updateTableData(searchField, sortRelevance());
				break;
			case 2:
				updateTableData(searchField, sortDate({ descending: true }));
				break;
			case 3:
				updateTableData(searchField, sortDate());
				break;
			case 4:
				updateTableData(searchField, sortTitle({ descending: true }));
				break;
			case 5:
				updateTableData(searchField, sortTitle());
				break;
			default:
				updateTableData(searchField, sortDate({ descending: true }));
				break;
		}
		setSortOptions(false);
	};

	/*
		On start, load all models sorted into the table
	*/
	useEffect(() => {
		if (modelList.items.length > 0) callSorting(1);
	}, [activeUser, modelList.items.length]);

	useEffect(() => {
		for (let index = 0; index < modelList.items.length; index++) {
			if (modelList.items[index].isFavorite) {
				setAnyModelPinned(true);
				break;
			} else if (index + 1 === modelList.items.length) {
				setAnyModelPinned(false);
			}
		}
	}, [modelList.items]);

	// define columns of model select table
	const columns = [
		// sortable title column with a Link to the model
		{
			title: t(['modelSelectView.model', '*Model']),
			dataIndex: 'title',
			key: 'title',
			render: (text, record) => <Link to={record.url}>{record.title}</Link>,
		},
		{
			title: t(['modelSelectView.date', '*Date']),
			dataIndex: 'date',
			key: 'date',
			render: (text) => text.substring(0, 10),
		},
	];

	// display the autocomplete box with search recommendations
	const searchRecommendations = (query = searchField) => {
		const searchFilteredModels = modelList.items.filter((model) =>
			model.displayTitle[i18n.language].toLowerCase().includes(query)
		);
		if (query && searchFilteredModels.length === 0) {
			// when there are no results, display this
			return [
				{
					label: renderTitle(t('search.NORESULTS')),
					options: [],
				},
			];
		} else if (!query) {
			// Do nothing
			return undefined;
		} else {
			return [
				{
					label: renderTitle('models'),
					options: searchFilteredModels.map((model) =>
						renderItem(model.displayTitle[i18n.language], model._id, `/model/${model.slug}/view/${model.homeView}`)
					),
				},
			];
		}
	};

	// function to search for the models, gts activated when pressed 'enter', searchbutton and little 'x' symbol on the right
	const searchModels = () => {
		setCurrentPage(1);
		updateTableData(searchField);
	};

	// function which opens the webpage
	const handleSelection = (value, option) => {
		navigate(option.link);
	};

	function ChangeText(viewVal) {
		if (viewVal) {
			return t('modelSelectView.gridview', 'Grid View');
		} else {
			return t('modelSelectView.listview', 'List View');
		}
	}

	// function for pagination and to sync the current page of grid and list view
	const handleChangeOfPage = (page) => {
		setCurrentPage(page);
	};

	const getDisplayedModels = () => {
		const convertedPage = currentPage - 1;
		let firstValue = PAGE_SIZE;
		firstValue *= convertedPage;
		const lastValue = firstValue + PAGE_SIZE;
		return filteredModels.slice(firstValue, lastValue);
	};

	// An eventhandler which leads to a rerender in case you change the text inside the searchbar.
	const onSearchChange = (event) => {
		setSearchField(event.target.value.toLowerCase());

		// everytime the query in the searchbar gets deleted all models will be displayed for once
		if (!event.target.value.toLowerCase()) {
			setCurrentPage(1);
			updateTableData('', sortRelevance());
		}
	};

	// render logo of the program, logo of Fraunhofer IPK and User Menu
	// render information that no models are available if tableData is defined but empty (which means that we actually worked with it and decided that there are no modles to show)
	//  else render sortable table with modelname and date
	return (
		<>
			<NormalNavbar />
			<Layout
				style={{
					height: 'calc(100vh - 64px)',
					backgroundImage: 'url("/pa/PA_Login_BG.png")',
					backgroundSize: '100% auto',
					WebkitBoxAlign: 'center',
					marginTop: '64px',
					overflowY: 'auto',
					overflowX: 'hidden',
				}}
			>
				<Skeleton loading={modelList.isFetching || modelList.init}>
					<div className={classes.content}>
						<div className={classes.fraunhoferLogoDiv}>
							<img className={classes.fraunhoferLogoImg} src='/pa/company_logo.png' alt='company logo' />
						</div>
						{!modelList.isFetching && modelList.items.length === 0 ? (
							<h2 className={classes.h2}>
								{user && user.name
									? t('modelSelectView.hnom', 'No models available')
									: t('modelSelectView.noPub', 'There are no public models.')}
								<br />
								{activeUser && activeUser.adminAccess && (
									<React.Fragment className={classes.linesOfContent}>
										<Button size='large' className={classes.buttons}>
											<Link to='/adminView/addModel'>{t('modelSelectView.aadd', 'Add new models')}</Link>
										</Button>
										<Button size='large' className={classes.buttons}>
											<Link to='/adminView/users'>{t('modelSelectView.mdla', 'Change model access')}</Link>
										</Button>
									</React.Fragment>
								)}
							</h2>
						) : tableData !== undefined && modelList.items.length > 0 ? (
							<>
								<div className={classes.topDiv}>
									<h2 className={classes.h3}>{t('modelSelectView.hsel', 'Select Model')}</h2>
									<AntdSearchBar
										onSearchChange={onSearchChange}
										searchModels={searchModels}
										handleSelection={handleSelection}
										query={searchField}
										searchRecommendations={searchRecommendations}
									/>
									<div className={classes.buttonDiv}>
										<OutsideAlerter setSortOptions={setSortOptions} sortOptions={sortOptions}>
											<div className={classes.buttonDivChild}>
												<button
													type='button'
													id='sort_button'
													className={classes.sortButton}
													onClick={openCloseSortDiv}
												>
													{!sortValue && <SortDescendingOutlined className={classes.viewIcon} />}
													{sortValue && <SortAscendingOutlined className={classes.viewIcon} />}
												</button>
												{!sortOptions && (
													<div className={classes.showMeaningSort}>
														<div className={classes.showMeaningPointer} />
														<div className={classes.showMeaningDiv}>{t('modelSelectView.sort', 'Sort')}</div>
													</div>
												)}
												<CustomSortButton
													sortOptions={sortOptions}
													sortValue={setSortValue}
													sortCall={callSorting}
													anyModelPinned={anyModelPinned}
												/>
											</div>
										</OutsideAlerter>
										<div className={classes.buttonDivChild}>
											<button type='button' className={classes.viewButton} onClick={changeViewValue}>
												{!viewValue && <MenuOutlined className={classes.viewIcon} />}
												{viewValue && <AppstoreOutlined className={classes.viewIcon} />}
											</button>
											<div className={classes.showMeaningView}>
												<div className={classes.showMeaningPointer} />
												<div className={classes.showMeaningDiv}>{ChangeText(viewValue)}</div>
											</div>
										</div>
									</div>
								</div>
								{!viewValue && (
									<div className={classes.gridView}>
										<CustomModelSelectCardList models={getDisplayedModels()} current={currentPage} />
									</div>
								)}
								{/* In case no models are left to show because they got filtered out and viewValue is false! */}
								{!viewValue && !filteredModels.length && (
									<h1 className={classes.notFoundH1}>{t('modelSelectView.noDataFound', 'No Data Found!')}</h1>
								)}
								{viewValue && (
									<Table
										loading={modelList.isFetching}
										columns={columns}
										dataSource={tableData}
										pagination={{
											hideOnSinglePage: true,
											position: ['bottomCenter'],
											current: currentPage,
											onChange: handleChangeOfPage,
										}}
										className={`${classes.tableLayout} ${classes.tableHead}`}
									/>
								)}
								{!viewValue && (
									<Pagination
										className={classes.pager}
										current={currentPage}
										pageSize={PAGE_SIZE}
										total={filteredModels.length}
										hideOnSinglePage
										onChange={handleChangeOfPage}
									/>
								)}
							</>
						) : null}
						<div width='100%' height='100%' />
					</div>
					<CustomFooter />
				</Skeleton>
			</Layout>
			)
		</>
	);
};

ModelSelectView.propTypes = { history: PropTypes.object };

const mapStateToProps = (state) => ({
	activeUser: state.activeUser,
	modelList: state.modelList,
	settings: state.settings,
});

export default connect(mapStateToProps)(ModelSelectView);
